import { readCmmdForgeRuntimeListSnapshot } from "../cmmd/cmmdForgeRuntimeListResource";
import { cmmdApiClient } from "../environments/cmmdApi";
import { getCurrentForgeWorkspaceId } from "../workspace/currentForgeWorkspaceStore";
import { resolveCurrentForgeWorkspace } from "../workspace/resolveCurrentForgeWorkspace";
import { resolveOwnRuntimeOrganizationId } from "../workspace/resolveOwnRuntimeOrganizationId";
import { parsePositiveCmmdId } from "./commandPaletteCmmdProject";

export interface CloudCmmdProjectPlacementCampaign {
  readonly id: string | number;
  readonly title: string | null;
  readonly workspaceId?: string | number | null;
}

export interface CloudCmmdProjectPlacementWorkspace {
  readonly id: string | number;
  readonly name: string;
}

/**
 * Where a new Forge Cloud project lands, using the palette's already-loaded
 * CMMD work intake -- the common path a real account hits, not the bootstrap
 * fallback below it in this file.
 *
 * A confirmed `currentForgeWorkspaceId` (CMMD-Center/forge#3572) always wins
 * over the work-intake's own guessed `workspace`, but only when a loaded
 * campaign confirms it has data for that workspace: getting placement wrong
 * once pins the new project to the wrong org permanently, since the project
 * record itself carries it from then on, so this refuses to guess a campaign
 * for a confirmed workspace it has no data for. That refusal (`campaignId`
 * null) throws, which routes the caller to
 * `resolveCloudCmmdProjectPlacementFromBootstrap` -- right for the org, silent
 * on the campaign -- rather than filing the project under an unrelated one.
 */
export function resolveDefaultCloudCmmdProjectPlacement(input: {
  readonly currentWorkspaceId: number | null;
  readonly workspace: CloudCmmdProjectPlacementWorkspace | null;
  readonly workspaces: ReadonlyArray<CloudCmmdProjectPlacementWorkspace>;
  readonly campaigns: ReadonlyArray<CloudCmmdProjectPlacementCampaign>;
}): {
  readonly organizationId: number;
  readonly campaignId: number;
  readonly workspaceName: string;
  readonly campaignName: string;
} {
  const fallbackWorkspaceId = parsePositiveCmmdId(input.workspace?.id);
  const workspaceId = input.currentWorkspaceId ?? fallbackWorkspaceId;
  const matchingCampaign = input.campaigns.find(
    (candidate) =>
      workspaceId !== null && parsePositiveCmmdId(candidate.workspaceId) === workspaceId,
  );
  // The "any campaign at all" fallback only applies when NEITHER signal
  // named a workspace -- the original, unscoped guess. Once a workspace is
  // confirmed, an unrelated campaign is worse than no campaign.
  const campaign = matchingCampaign ?? (workspaceId === null ? input.campaigns[0] : undefined);
  const organizationId = workspaceId ?? parsePositiveCmmdId(campaign?.workspaceId);
  const campaignId = parsePositiveCmmdId(campaign?.id);
  if (!organizationId || !campaignId) {
    throw new Error(
      "Forge Cloud workspace creation needs an active CMMD workspace and campaign. Refresh CMMD, then try again.",
    );
  }
  const resolvedWorkspaceName = input.workspaces.find(
    (candidate) => parsePositiveCmmdId(candidate.id) === organizationId,
  )?.name;
  return {
    organizationId,
    campaignId,
    workspaceName: resolvedWorkspaceName ?? input.workspace?.name ?? "CMMD",
    campaignName: campaign?.title ?? "CMMD",
  };
}

/**
 * Where a new Forge Cloud project lands when the palette's cached CMMD work
 * intake cannot answer: read the workspace straight from CMMD's bootstrap.
 *
 * A confirmed `currentForgeWorkspaceId` (CMMD-Center/forge#3572) always wins
 * when the account has one: getting this wrong once pins the new project to
 * the wrong org permanently, since the project record itself carries it from
 * then on. When nothing has been confirmed yet, this refuses to guess
 * `bootstrap.activeWorkspace ?? workspaces[0]`. `activeWorkspace` is CMMD's
 * `organizationMembers.isDefault` answer, which tracks join history, not the
 * workspace the account is actually using, the same reason
 * `resolveCurrentForgeWorkspace` never consults it. Instead: the account's
 * own runtime catalog names an unambiguous organization (a fact), or the
 * account has exactly one CMMD workspace to begin with (not a guess, nothing
 * to choose between); anything more ambiguous than that throws rather than
 * filing the project under a workspace nobody confirmed.
 *
 * Campaign is null on purpose. Bootstrap knows workspaces, not campaigns, and
 * inventing one here would file the project under a campaign nobody chose.
 */
export async function resolveCloudCmmdProjectPlacementFromBootstrap(): Promise<{
  readonly organizationId: number;
  readonly campaignId: null;
  readonly workspaceName: string;
  readonly campaignName: null;
}> {
  const currentWorkspaceId = getCurrentForgeWorkspaceId();
  const bootstrap = await cmmdApiClient.getBootstrap(
    currentWorkspaceId !== null ? { organizationId: currentWorkspaceId } : undefined,
  );
  const confirmed =
    currentWorkspaceId !== null
      ? bootstrap.workspaces.find((candidate) => candidate.id === currentWorkspaceId)
      : undefined;
  // A confirmed pick this bootstrap response doesn't carry -- a stale pick,
  // or a membership CMMD has since revoked -- must not fall through to the
  // deterministic fallback below: that would file the new project under a
  // workspace the account never confirmed, permanently (CodeRabbit review,
  // CMMD-Center/forge#3572).
  if (currentWorkspaceId !== null && !confirmed) {
    throw new Error(
      "Forge Cloud workspace creation needs the confirmed CMMD workspace, and CMMD's bootstrap no longer lists it. Refresh CMMD, then try again.",
    );
  }
  const workspace =
    confirmed ??
    resolveDeterministicFallbackWorkspace({
      userId: bootstrap.identity.id,
      workspaces: bootstrap.workspaces,
    });
  const organizationId = parsePositiveCmmdId(workspace?.id);
  if (!organizationId) {
    throw new Error(
      "Forge Cloud workspace creation needs a CMMD workspace. Pick one from the workspace switcher, then try again.",
    );
  }
  return {
    organizationId,
    campaignId: null,
    workspaceName: workspace?.name ?? "CMMD",
    campaignName: null,
  };
}

/**
 * `resolveCurrentForgeWorkspace`'s own deterministic answer, seeded with the
 * account's own sole runtime organization (`resolveOwnRuntimeOrganizationId`)
 * instead of a stored pick (there is none here, by construction: the caller
 * only reaches this once `confirmed` comes back empty). Ambiguous cases
 * (`"ask"`, multiple memberships and no runtime fact to break the tie) and
 * the zero-membership case (`"none"`) both return null: this path has no
 * "ask" UI of its own, so it refuses rather than guessing.
 */
function resolveDeterministicFallbackWorkspace(input: {
  readonly userId: number;
  readonly workspaces: ReadonlyArray<{ readonly id: number; readonly name: string }>;
}): { readonly id: number; readonly name: string } | null {
  const resolution = resolveCurrentForgeWorkspace({
    storedWorkspaceId: resolveOwnRuntimeOrganizationId({
      userId: input.userId,
      environments: readCmmdForgeRuntimeListSnapshot().environments ?? [],
    }),
    workspaces: input.workspaces,
  });
  if (resolution.kind !== "resolved") return null;
  return { id: resolution.organizationId, name: resolution.workspaceName };
}
